import {
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useInterval,
  VStack,
} from '@chakra-ui/react';
import AccountCircleSharpIcon from '@gamma/ui/icons/AccountCircleSharpIcon';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import HelpSharpIcon from '@gamma/ui/icons/HelpSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { safeParse } from 'secure-json-parse';
import { PageHeader } from '../../../../shared/components';
import { useDateRangeContext } from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { useQueryDetectionsTotalItems } from '../../queries';

import { DetectionQueryVariables } from '../../pages';

const { controls, detection } = i18n.pages.detections;

const { assignTo, closeDetection, suppressEntity } = controls;

const {
  detection: detectionHeading,
  detectionDescription,
  performActions,
  tuneSystem,
  updateStatus,
  updateAssignment,
} = detection;

export interface DetectionsPageHeaderProps {
  queryVariables?: DetectionQueryVariables;
  isLoading?: boolean;
  onDateRangeChange?: () => void;
}

export const formatTotalItemsQuery = ({
  startDate,
  endDate,
  query,
}: {
  startDate: number;
  endDate: number;
  query: string;
}) => {
  if (!query) {
    return '';
  }

  const queryObject = safeParse(query);

  const mustArray = queryObject?.query?.bool?.must;

  const updatedMustArray = mustArray?.map(
    (item: {
      range?: {
        latest_start_timestamp?: { gte?: number };
        earliest_start_timestamp?: { lte?: number };
      };
    }) => {
      if (item?.range?.latest_start_timestamp) {
        item.range.latest_start_timestamp.gte = startDate;
      } else if (item?.range?.earliest_start_timestamp) {
        item.range.earliest_start_timestamp.lte = endDate;
      }
      return item;
    },
  );

  const resultQuery = JSON.stringify({
    query: {
      bool: {
        must: updatedMustArray,
      },
    },
  });

  return resultQuery;
};

export const DetectionsPageHeader = ({
  queryVariables,
  isLoading,
  onDateRangeChange,
}: DetectionsPageHeaderProps) => {
  const { showCustomDateRangePicker } = useDateRangeContext();
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentTimeStamp, setCurrentTimeStamp] = useState<number>(
    moment().unix(),
  );
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const date_end = params.get('end');

  const totalItemsQuery = useMemo(
    () =>
      formatTotalItemsQuery({
        startDate: Number(date_end),
        endDate: currentTimeStamp,
        query: queryVariables?.query ?? '',
      }),
    [currentTimeStamp, queryVariables, date_end],
  );

  useQueryDetectionsTotalItems({
    skip: !queryVariables?.query || !totalItemsQuery || !currentTimeStamp,
    variables: {
      ...queryVariables,
      query: totalItemsQuery,
    },
    onCompleted: (data) => {
      if (
        isMounted.current &&
        totalItems !== data?.queryDetectionsPaginated?.total_items
      ) {
        setTotalItems(data?.queryDetectionsPaginated?.total_items ?? 0);
      }
    },
  });

  useInterval(
    () => setCurrentTimeStamp(moment().unix()),
    showCustomDateRangePicker ? null : 15000,
  );

  return (
    <PageHeader
      totalItems={totalItems}
      isLoading={isLoading}
      onDateRangeChange={onDateRangeChange}
    >
      <Flex alignItems="center">
        <Text mr={2}>{i18n.navigation.detections}</Text>
        <Popover isLazy trigger="hover" openDelay={300}>
          <PopoverTrigger>
            <span>
              <HelpSharpIcon size="sm" />
            </span>
          </PopoverTrigger>
          <PopoverContent w="332px">
            <PopoverArrow />
            <PopoverCloseButton fontSize="12px" size="sm" />
            <PopoverHeader>
              <Text textStyle="body-md" fontWeight="600">
                {detectionHeading}
              </Text>
            </PopoverHeader>
            <PopoverBody pb={4}>
              <Text as="p" mb={2} textStyle="body-md">
                {detectionDescription}
              </Text>
              <Text as="p" mb={2} textStyle="body-md">
                {performActions}:
              </Text>
              <VStack alignItems="flex-start">
                <HStack>
                  <CheckCircleSharpIcon />
                  <Text textStyle="body-md" fontWeight="600">
                    {closeDetection}:
                  </Text>
                  <Text textStyle="body-md">{updateStatus}</Text>
                </HStack>
                <HStack>
                  <VisibilitySharpIcon />
                  <Text textStyle="body-md" fontWeight="600">
                    {suppressEntity}:
                  </Text>
                  <Text textStyle="body-md">{tuneSystem}</Text>
                </HStack>
                <HStack>
                  <AccountCircleSharpIcon />
                  <Text textStyle="body-md" fontWeight="600">
                    {assignTo}:
                  </Text>
                  <Text textStyle="body-md">{updateAssignment}</Text>
                </HStack>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
    </PageHeader>
  );
};
